import { useEffect, useRef } from 'react';

import { useFeedState, useFeedActions } from './FeedContext';

import { getPosts } from './restApi';

const FeedLoader = () => {
  const state = useFeedState();
  const { initializeState, onResponse, onError } = useFeedActions();
  const initializedRef = useRef(false);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      initializeState();
    }
  }, []);

  useEffect(() => {
    if (!state?.viewState || fetchingRef.current) return;

    const postData = state?.postDataByViewState?.[state?.viewState] || [];
    if (postData.length === 0 && (state?.canViewMorePostsByViewState?.[state?.viewState] ?? true)) {
      fetchingRef.current = true;
      getPosts({
        lastPageViewed: state?.lastPageViewedByViewState?.[state?.viewState] || 0,
        viewState: state?.viewState || 'approved',
        onResponse: (data) => {
          fetchingRef.current = false;
          onResponse(data);
        },
        onError: (feedback) => {
          fetchingRef.current = false;
          onError(feedback);
        },
      });
    }
  // state?.postDataByViewState is included as a dependency for the initial load
  // sequence where viewState doesn't change but post data goes from empty to
  // populated:
  //
  // 1. Component mounts, state is {} (empty object from AppContext)
  // 2. First effect runs initializeState(), which sets viewState to 'approved'
  // 3. viewState goes from undefined → 'approved', so the second effect fires
  // 4. postData is empty, so it calls getPosts
  // 5. The fetch completes, onResponse appends posts to postDataByViewState.approved
  // 6. viewState is still 'approved' — it hasn't changed
  //
  // At step 6, without postDataByViewState in the dependency array, the effect
  // wouldn't re-run. That's actually fine in the happy path — we don't want it
  // to re-run since posts already loaded. But the effect's closure from step 3
  // still has the old empty postData. If anything else causes a re-render and
  // React decides to re-run the effect (e.g., strict mode), it would see stale
  // data and fetch again.
  //
  // So honestly — it's mostly defensive. The fetchingRef is doing the heavy
  // lifting to prevent duplicate fetches. This dependency just ensures the
  // effect's closure has fresh data if it does re-run, making the effect more
  // correct in terms of React's mental model — your dependencies should reflect
  // what the effect actually reads.
  }, [state?.viewState, state?.postDataByViewState]);

  return null;
};

export default FeedLoader;
